import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarClock, FileText, Minus, Plus, RotateCcw, Satellite, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

import PlanReservationModal from "@/components/PlanReservationModal";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { api } from "@/lib/api";


function boundsFor(points) {
  const xs = points.map((point) => point[0]);
  const ys = points.map((point) => point[1]);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
  };
}

function OverlayShape({ layerId, shape }) {
  if (shape.type === "line") {
    return (
      <line
        x1={shape.points[0][0]}
        x2={shape.points[1][0]}
        y1={shape.points[0][1]}
        y2={shape.points[1][1]}
      />
    );
  }
  if (shape.type === "circle") {
    return <circle cx={shape.center[0]} cy={shape.center[1]} r={shape.radius} />;
  }
  if (shape.type === "text") {
    const isDimension = layerId === "COTA_MZA_LOTE";
    const textScale = isDimension ? 0.85 : 1.8;
    return (
      <text
        fontSize={shape.height * textScale}
        textAnchor={isDimension ? "middle" : "start"}
        x={shape.point[0]}
        y={shape.point[1]}
      >
        {shape.value}
      </text>
    );
  }
  const points = shape.points.map((point) => point.join(",")).join(" ");
  return shape.type === "polygon" ? <polygon points={points} /> : <polyline points={points} />;
}

function texturePatternId(layerId) {
  return `layer-texture-${layerId.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
}

function LayerPattern({ layer }) {
  const patternId = texturePatternId(layer.id);
  const color = layer.color;
  if (layer.texture === "paver") {
    return (
      <pattern height="28" id={patternId} patternUnits="userSpaceOnUse" width="28">
        <rect fill={color} height="28" width="28" />
        <path
          d="M0 14H28M14 0V14M0 0V14M14 14V28"
          fill="none"
          opacity="0.5"
          stroke="#6f3525"
          strokeWidth="1.3"
        />
      </pattern>
    );
  }
  if (layer.texture === "natural") {
    return (
      <pattern height="24" id={patternId} patternUnits="userSpaceOnUse" width="24">
        <rect fill={color} height="24" width="24" />
        <circle cx="5" cy="6" fill="#5f442f" opacity="0.38" r="1.2" />
        <circle cx="17" cy="14" fill="#5f442f" opacity="0.32" r="1" />
        <path
          d="M3 20L10 17M15 4L22 2"
          opacity="0.35"
          stroke="#6e4d34"
          strokeWidth="0.8"
        />
      </pattern>
    );
  }
  if (layer.texture === "waves") {
    return (
      <pattern height="24" id={patternId} patternUnits="userSpaceOnUse" width="38">
        <rect fill={color} height="24" width="38" />
        <path
          d="M0 7C5 2 12 2 18 7S31 12 38 7M0 18C5 13 12 13 18 18S31 23 38 18"
          fill="none"
          opacity="0.62"
          stroke="#d9f5ff"
          strokeWidth="1.5"
        />
      </pattern>
    );
  }
  if (layer.texture === "trees") {
    return (
      <pattern height="32" id={patternId} patternUnits="userSpaceOnUse" width="32">
        <rect fill={color} height="32" width="32" />
        <circle cx="8" cy="9" fill="#2d6b45" opacity="0.65" r="4" />
        <circle cx="23" cy="20" fill="#2d6b45" opacity="0.65" r="4.5" />
        <path d="M8 13V17M23 24V28" stroke="#795b36" strokeWidth="1.4" />
      </pattern>
    );
  }
  if (layer.texture === "hatch") {
    return (
      <pattern height="14" id={patternId} patternUnits="userSpaceOnUse" width="14">
        <rect fill={color} height="14" width="14" />
        <path
          d="M-3 14L14 -3M4 17L17 4"
          opacity="0.46"
          stroke="#ffffff"
          strokeWidth="1.1"
        />
      </pattern>
    );
  }
  return null;
}

export default function PlanPage() {
  const { user } = useAuth();
  const { formatAmount } = useCurrency();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [lots, setLots] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [overlays, setOverlays] = useState({ layers: [] });
  const [layerSettings, setLayerSettings] = useState([]);
  const [selected, setSelected] = useState(null);
  const [viewBox, setViewBox] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [reservationLot, setReservationLot] = useState(null);
  const [error, setError] = useState("");
  const svgRef = useRef(null);
  const pointersRef = useRef(new Map());
  const dragRef = useRef(null);
  const pinchRef = useRef(null);
  const suppressClickRef = useRef(false);

  useEffect(() => {
    Promise.all([
      fetch("/plan-preview.json").then((response) => response.json()),
      fetch("/plan-overlays.json").then((response) => response.json()),
      api.get("/lots"),
      api.get("/settings/plan"),
      api.get("/settings/lot-statuses"),
    ])
      .then(([planData, overlayData, lotResponse, settingsResponse, statusResponse]) => {
        setPlan(planData);
        setOverlays(overlayData);
        setLots(lotResponse.data);
        setLayerSettings(settingsResponse.data.layers);
        setStatuses(statusResponse.data);
        setViewBox(`0 0 ${planData.bounds.width} ${planData.bounds.height}`);
      })
      .catch(() => setError("No fue posible cargar el plano comercial."));
  }, []);

  const lotByPlan = useMemo(() => new Map(lots.map((lot) => [lot.plan_id, lot])), [lots]);
  const statusByName = useMemo(
    () => new Map(statuses.map((status) => [status.name, status])),
    [statuses],
  );
  const defaultViewBox = useMemo(() => {
    return plan ? `0 0 ${plan.bounds.width} ${plan.bounds.height}` : "0 0 1 1";
  }, [plan]);
  const selectedLot = selected ? lotByPlan.get(selected.id) : null;
  const counts = useMemo(() => lots.reduce((total, lot) => ({
    ...total,
    [lot.estado]: (total[lot.estado] || 0) + 1,
  }), {}), [lots]);

  const toggleStatusFilter = (statusName) => {
  setSelected(null);

  setStatusFilter((current) => {
    if (current === statusName) {
      return null;
    }

    return statusName;
  });
};

  const visibleLayers = useMemo(() => {
  if (!viewBox || !plan) return new Set();

  const width = Number(viewBox.split(" ")[2]);
  const zoomLevel = plan.bounds.width / width;

  return new Set(
    layerSettings
      .filter((layer) => {
        if (!layer.enabled) return false;

        // Las capas de texto y dimensiones siguen
        // respetando el nivel de zoom configurado.
        if (
          layer.category === "label" ||
          layer.category === "dimension"
        ) {
          return zoomLevel >= layer.min_zoom;
        }

        // Las capas gráficas principales del plano
        // permanecen visibles incluso al alejar el zoom.
        return true;
      })
      .map((layer) => layer.id),
  );
}, [layerSettings, plan, viewBox]);
  const layerSettingById = useMemo(() => {
    return new Map(layerSettings.map((layer) => [layer.id, layer]));
  }, [layerSettings]);
  const mapViewport = useMemo(() => {
    if (!viewBox) return null;
    const [x, y, width, height] = viewBox.split(" ").map(Number);
    return { x, y, width, height };
  }, [viewBox]);
  const activeOverlays = overlays.layers.filter((layer) => visibleLayers.has(layer.id));
  const renderOverlay = (layer) => {
    const style = layerSettingById.get(layer.id);
    const fill = style?.texture === "solid"
      ? style.color
      : `url(#${texturePatternId(layer.id)})`;
    return (
      <g
        className={`plan-overlay layer-${layer.category}`}
        data-testid={`plan-layer-${layer.id}`}
        key={layer.id}
        style={{
          "--layer-color": style?.color || "#5B7C91",
          "--layer-fill": fill || "#5B7C91",
          "--layer-opacity": style?.opacity || 0.8,
        }}
      >
        {layer.shapes
          .filter((shape) => {
            if (!mapViewport || shape.type !== "text") return true;
            const [x, y] = shape.point;
            return (
              x >= mapViewport.x
              && x <= mapViewport.x + mapViewport.width
              && y >= mapViewport.y
              && y <= mapViewport.y + mapViewport.height
            );
          })
          .map((shape, index) => (
            <OverlayShape key={index} layerId={layer.id} shape={shape} />
          ))}
      </g>
    );
  };

  const focusLot = (parcel) => {
    const bounds = boundsFor(parcel.points);
    const width = Math.max(bounds.maxX - bounds.minX, 35) * 7;
    const height = Math.max(bounds.maxY - bounds.minY, 35) * 7;
    const centerX = (bounds.minX + bounds.maxX) / 2;
    const centerY = (bounds.minY + bounds.maxY) / 2;
    setSelected(parcel);
    setViewBox(`${centerX - width / 2} ${centerY - height / 2} ${width} ${height}`);
  };

  const zoom = (factor) => {
    setViewBox((current) => {
      if (!current) return current;
      const [x, y, width, height] = current.split(" ").map(Number);
      const nextWidth = width * factor;
      const nextHeight = height * factor;
      const nextX = x + (width - nextWidth) / 2;
      const nextY = y + (height - nextHeight) / 2;
      return `${nextX} ${nextY} ${nextWidth} ${nextHeight}`;
    });
  };

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return undefined;
    const onWheel = (event) => {
      event.preventDefault();
      zoom(event.deltaY > 0 ? 1.25 : 0.8);
    };
    svg.addEventListener("wheel", onWheel, { passive: false });
    return () => svg.removeEventListener("wheel", onWheel);
  }, [plan]);

      const handlePointerDown = (event) => {
    const svg = svgRef.current;

    if (!svg || !viewBox) return;

suppressClickRef.current = false;

// El mouse debe conservar el click normal.
// Touch y stylus sí necesitan captura para
// mantener correctamente el gesto.
if (event.pointerType !== "mouse") {
  event.preventDefault();
  svg.setPointerCapture?.(event.pointerId);
}

pointersRef.current.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
    });

    // =========================================
    // DOS DEDOS → INICIAR PINCH
    // =========================================
    if (pointersRef.current.size === 2) {
      const points = Array.from(
        pointersRef.current.values(),
      );

      const distance = Math.max(
        Math.hypot(
          points[0].x - points[1].x,
          points[0].y - points[1].y,
        ),
        1,
      );

      const centerX =
        (points[0].x + points[1].x) / 2;

      const centerY =
        (points[0].y + points[1].y) / 2;

      pinchRef.current = {
        initialDistance: distance,
        initialCenterX: centerX,
        initialCenterY: centerY,
        initialViewBox: viewBox,
      };

      dragRef.current = null;
      suppressClickRef.current = true;

      return;
    }

    // =========================================
    // UN DEDO → INICIAR ARRASTRE
    // =========================================
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      initialViewBox: viewBox,
      moved: false,
    };
  };

  const handlePointerMove = (event) => {
    const svg = svgRef.current;

    if (!svg || !viewBox) return;

    if (!pointersRef.current.has(event.pointerId)) {
      return;
    }

    event.preventDefault();

    pointersRef.current.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
    });

    const rect = svg.getBoundingClientRect();

    // =========================================
    // DOS DEDOS → PINCH ZOOM
    // =========================================
    if (
      pointersRef.current.size === 2 &&
      pinchRef.current
    ) {
      const points = Array.from(
        pointersRef.current.values(),
      );

      const distance = Math.max(
        Math.hypot(
          points[0].x - points[1].x,
          points[0].y - points[1].y,
        ),
        1,
      );

      const {
        initialDistance,
        initialCenterX,
        initialCenterY,
        initialViewBox,
      } = pinchRef.current;

      const [
        x,
        y,
        width,
        height,
      ] = initialViewBox
        .split(" ")
        .map(Number);

      const scale =
        initialDistance / distance;

      const nextWidth = width * scale;
      const nextHeight = height * scale;

      const ratioX =
        (initialCenterX - rect.left) /
        rect.width;

      const ratioY =
        (initialCenterY - rect.top) /
        rect.height;

      const centerX =
        x + width * ratioX;

      const centerY =
        y + height * ratioY;

      const nextX =
        centerX -
        nextWidth * ratioX;

      const nextY =
        centerY -
        nextHeight * ratioY;

      setViewBox(
        `${nextX} ${nextY} ${nextWidth} ${nextHeight}`,
      );

      return;
    }

    // =========================================
    // UN DEDO → ARRASTRE
    // =========================================
    if (!dragRef.current) return;

    const drag = dragRef.current;

    const dx =
      event.clientX - drag.startX;

    const dy =
      event.clientY - drag.startY;

    // Evita confundir un pequeño movimiento
    // con un arrastre.
    if (
      Math.abs(dx) > 5 ||
      Math.abs(dy) > 5
    ) {
      drag.moved = true;
      suppressClickRef.current = true;
    }

    const [
      x,
      y,
      width,
      height,
    ] = drag.initialViewBox
      .split(" ")
      .map(Number);

    const shiftX =
      dx * width / rect.width;

    const shiftY =
      dy * height / rect.height;

    setViewBox(
      `${x - shiftX} ${y - shiftY} ${width} ${height}`,
    );
  };

  const handlePointerUp = (event) => {
    pointersRef.current.delete(
      event.pointerId,
    );

    try {
      svgRef.current?.releasePointerCapture?.(
        event.pointerId,
      );
    } catch {
      // El puntero ya fue liberado.
    }

    if (
  pointersRef.current.size === 0
) {
  const wasDragging =
    dragRef.current?.moved === true;

  dragRef.current = null;

  if (wasDragging) {
    suppressClickRef.current = true;

    setTimeout(() => {
      suppressClickRef.current = false;
    }, 50);
  }
}
  };

  const handlePointerCancel = (event) => {
    handlePointerUp(event);
  };
  const resetView = () => {
    setSelected(null);
    setViewBox(defaultViewBox);
  };

  const updateReservedLot = (lot) => {
    setLots((current) => current.map((item) => (item.id === lot.id ? lot : item)));
  };

  if (error) {
    return (
      <div className="page-loading" data-testid="plan-error-state">
        {error}
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="page-loading" data-testid="plan-loading-state">
        Cargando plano comercial...
      </div>
    );
  }

  const selectedStatus = selectedLot
    ? statusByName.get(selectedLot.estado)
    : null;
  const canReserve = selectedLot
    && selectedStatus?.vendor_can_sell
    && selectedStatus?.category === "disponible"
    && (user.role === "vendedor" || user.role === "administrador");

  const selectedLotPanel = selectedLot ? (
    <>
      <div className="plan-detail-header">
        <span className="eyebrow">LOTE SELECCIONADO</span>
        <button aria-label="Cerrar detalle de lote" className="icon-button" data-testid="close-plan-lot-panel" onClick={() => setSelected(null)} type="button"><X size={19} /></button>
      </div>
      <h2>Manzano {selectedLot.manzano} · Lote {selectedLot.lote}</h2>
      <span
        className="status-tag"
        data-testid="plan-selected-lot-status"
        style={{ backgroundColor: selectedStatus?.color, color: "#173f5e" }}
      >
        {selectedLot.estado}
      </span>
      <dl className="plan-lot-details" data-testid="plan-selected-lot-details">
        <div>
          <dt>Superficie</dt>
          <dd>{selectedLot.superficie} m²</dd>
        </div>
        <div>
          <dt>Precio por m²</dt>
          <dd>{formatAmount(selectedLot.precio_m2)}</dd>
        </div>
        <div>
          <dt>Precio total</dt>
          <dd>{formatAmount(selectedLot.precio_total)}</dd>
        </div>
        <div>
          <dt>Fase</dt>
          <dd>{selectedLot.fase || "—"}</dd>
        </div>
      </dl>
      {selectedLot.observaciones && <p>{selectedLot.observaciones}</p>}
      <button
        className="outline-button plan-quote-button"
        data-testid="quote-from-plan-button"
        onClick={() => navigate("/cotizador", { state: { lot: selectedLot } })}
        type="button"
      >
        <FileText size={17} />
        Cotizar lote
      </button>
      {canReserve && (
        <button
          className="primary-button"
          data-testid="reserve-from-plan-button"
          onClick={() => setReservationLot(selectedLot)}
          type="button"
        >
          <CalendarClock size={17} />
          Iniciar reserva
        </button>
      )}
    </>
  ) : null;

  return (
    <section className="plan-fullscreen-page" data-testid="plan-page">
      <div
  className="plan-status-legend"
  data-testid="plan-status-legend"
>
  {statuses.map((status) => {
    const active =
      statusFilter === status.name;

    return (
      <button
        className={`plan-status-filter${active ? " active" : ""}`}
        data-testid={`plan-status-${status.id}`}
        key={status.id}
        onClick={() =>
          toggleStatusFilter(status.name)
        }
        style={{
          backgroundColor: status.color,
          color: "#173f5e",
        }}
        type="button"
        title={
          active
            ? "Mostrar todos los lotes"
            : `Mostrar solo ${status.name.toLowerCase()}`
        }
      >
        {counts[status.name] || 0}{" "}
        {status.name.toLowerCase()}
      </button>
    );
  })}
</div>
      <section className="plan-canvas-panel" data-testid="plan-canvas-panel">
          <div className="plan-toolbar">
            <div>
              <button
                aria-label="Alejar plano"
                className="icon-button"
                data-testid="plan-zoom-out"
                onClick={() => zoom(1.45)}
                type="button"
              >
                <Minus size={17} />
              </button>
              <button
                aria-label="Acercar plano"
                className="icon-button"
                data-testid="plan-zoom-in"
                onClick={() => zoom(0.7)}
                type="button"
              >
                <Plus size={17} />
              </button>
              <button
                aria-label="Restablecer vista"
                className="icon-button"
                data-testid="plan-reset-view"
                onClick={resetView}
                type="button"
              >
                <RotateCcw size={17} />
              </button>
              <button
                aria-label="Vista satelital pendiente"
                className="icon-button satellite-button"
                data-testid="plan-satellite-button"
                disabled
                title="Pendiente de clave de Google Maps y archivo georreferenciado"
                type="button"
              >
                <Satellite size={17} />
              </button>
            </div>
          </div>
                    <svg
            className="plan-svg"
            data-testid="plan-svg"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            preserveAspectRatio="xMidYMid meet"
            ref={svgRef}
            viewBox={viewBox}
            style={{
              touchAction: "none",
              userSelect: "none",
              WebkitUserSelect: "none",
              cursor: "grab",
            }}
          >
            <defs>
              {layerSettings.map((layer) => (
                <LayerPattern key={layer.id} layer={layer} />
              ))}
            </defs>
            <rect
              fill="#eff7fb"
              height={plan.bounds.height}
              width={plan.bounds.width}
              x="0"
              y="0"
            />
            {activeOverlays
              .filter((layer) => !["label", "dimension"].includes(layer.category))
              .map(renderOverlay)}
            {plan.lots
  .filter((parcel) => {
    if (!statusFilter) {
      return true;
    }

    const lot = lotByPlan.get(parcel.id);

    return lot?.estado === statusFilter;
  })
  .map((parcel) => {
    const lot = lotByPlan.get(parcel.id);
    const status = statusByName.get(lot?.estado);
              const selectedClass = selected?.id === parcel.id ? " selected" : "";
              const points = parcel.points.map((point) => point.join(",")).join(" ");
              return (
                               <polygon
  className={`plan-lot${selectedClass}`}
  data-testid={`plan-lot-${parcel.id}`}
  key={parcel.id}
  onClick={() => focusLot(parcel)}
                  points={points}
                  style={{
                    "--lot-color":
                      status?.color || "#6A8292",
                  }}
                />
              );
            })}
            {activeOverlays
              .filter((layer) => ["label", "dimension"].includes(layer.category))
              .map(renderOverlay)}
          </svg>
      </section>
      {selectedLotPanel && <aside className="plan-side-panel" data-testid="plan-side-panel">{selectedLotPanel}</aside>}
      {reservationLot && (
        <PlanReservationModal
          lot={reservationLot}
          onClose={() => setReservationLot(null)}
          onReserved={updateReservedLot}
        />
      )}
    </section>
  );
}
